import { useEffect, useCallback, useRef } from 'react';

const STORAGE_PREFIX = 'autopod-position-';
const SAVE_INTERVAL_MS = 5000; // Save every 5 seconds
const MIN_POSITION_SECONDS = 30; // Don't save if < 30s
const MAX_PROGRESS_PERCENT = 0.95; // Don't save if > 95% complete
const MAX_AGE_DAYS = 30; // Clean up positions older than 30 days

interface SavedPosition {
  position: number;
  timestamp: number;
  duration: number;
}

/**
 * Get the storage key for an episode
 */
function getStorageKey(episodeId: number): string {
  return `${STORAGE_PREFIX}${episodeId}`;
}

/**
 * Load a saved position from localStorage
 */
export function loadSavedPosition(episodeId: number): number | null {
  try {
    const stored = localStorage.getItem(getStorageKey(episodeId));
    if (!stored) return null;

    const data: SavedPosition = JSON.parse(stored);

    // Check if it's too old
    const ageMs = Date.now() - data.timestamp;
    if (ageMs > MAX_AGE_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(getStorageKey(episodeId));
      return null;
    }

    return data.position;
  } catch {
    return null;
  }
}

/**
 * Save a position to localStorage
 */
function savePosition(episodeId: number, position: number, duration: number): void {
  // Don't save if position is too early or too late
  if (position < MIN_POSITION_SECONDS) return;
  if (duration > 0 && position / duration > MAX_PROGRESS_PERCENT) return;

  try {
    const data: SavedPosition = {
      position,
      timestamp: Date.now(),
      duration,
    };
    localStorage.setItem(getStorageKey(episodeId), JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear a saved position (e.g., when episode finishes)
 */
export function clearSavedPosition(episodeId: number): void {
  try {
    localStorage.removeItem(getStorageKey(episodeId));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clean up old saved positions (older than MAX_AGE_DAYS)
 */
export function cleanupOldPositions(): void {
  try {
    const now = Date.now();
    const maxAge = MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const data: SavedPosition = JSON.parse(localStorage.getItem(key) || '');
          if (now - data.timestamp > maxAge) {
            keysToRemove.push(key);
          }
        } catch {
          // Invalid data, remove it
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore errors during cleanup
  }
}

interface UsePlaybackPositionOptions {
  episodeId: number;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isLive?: boolean;
}

/**
 * Hook to automatically save and manage playback position
 */
export function usePlaybackPosition({
  episodeId,
  currentTime,
  duration,
  isPlaying,
  isLive = false,
}: UsePlaybackPositionOptions): void {
  const lastSaveRef = useRef(0);
  const episodeIdRef = useRef(episodeId);

  // Update episode ID ref
  useEffect(() => {
    episodeIdRef.current = episodeId;
  }, [episodeId]);

  // Save position on interval while playing
  useEffect(() => {
    if (isLive || !isPlaying) return;

    const interval = setInterval(() => {
      if (Date.now() - lastSaveRef.current >= SAVE_INTERVAL_MS) {
        savePosition(episodeIdRef.current, currentTime, duration);
        lastSaveRef.current = Date.now();
      }
    }, SAVE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isPlaying, isLive, currentTime, duration]);

  // Save position on pause
  const handlePause = useCallback(() => {
    if (!isLive && episodeIdRef.current) {
      savePosition(episodeIdRef.current, currentTime, duration);
    }
  }, [isLive, currentTime, duration]);

  useEffect(() => {
    if (!isPlaying && !isLive) {
      handlePause();
    }
  }, [isPlaying, isLive, handlePause]);

  // Save position on beforeunload and visibilitychange
  useEffect(() => {
    if (isLive) return;

    const handleBeforeUnload = () => {
      savePosition(episodeIdRef.current, currentTime, duration);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        savePosition(episodeIdRef.current, currentTime, duration);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLive, currentTime, duration]);

  // Clean up old positions on mount
  useEffect(() => {
    cleanupOldPositions();
  }, []);
}
